import { DateField } from '@/design-system/ui/DateField';
import { Input } from '@/design-system/ui/Input';
import { SimpleSelect } from '@/design-system/ui/Select';
import {
  CHURCH_STATUS_HINT,
  CHURCH_STATUS_LABEL,
  CHURCH_STATUS_ORDER,
  JOINED_VIA_HINT,
  JOINED_VIA_LABEL,
  membershipLabel,
  showsOriginChurch,
} from '@escala/domain/members';
import type { ChurchStatus, JoinedVia, Member } from '@escala/domain/types';

import FormField, { fieldErrorProps } from './FormField';

type ChurchData = Pick<Member, 'churchStatus' | 'memberSince' | 'joinedVia' | 'baptismDate' | 'originChurch'>;

const NONE = 'none';

/** As opções (na ordem de `keys`) com o que cada uma quer dizer embaixo do nome. */
const options = <T extends string>(
  keys: readonly T[],
  labels: Record<T, string>,
  hints: Record<T, string>
) => [
  { value: NONE, label: 'Não informado', pin: true },
  ...keys.map((value) => ({ value, label: labels[value], description: hints[value] })),
];

// Fora do componente: a mesma lista a cada render (o `SimpleSelect` só reordena quando ela muda).
// A situação vai na ordem do domínio (a mais provável primeiro); a forma de ingresso, alfabética.
const STATUS_OPTIONS = options(CHURCH_STATUS_ORDER, CHURCH_STATUS_LABEL, CHURCH_STATUS_HINT);
const JOINED_OPTIONS = options(
  Object.keys(JOINED_VIA_LABEL) as JoinedVia[],
  JOINED_VIA_LABEL,
  JOINED_VIA_HINT
);

/**
 * Vida na igreja: situação, "Membro desde" (com o tempo de membresia), forma de ingresso e
 * batismo — a situação e a forma de ingresso com o que cada opção quer dizer. "Igreja de origem" só aparece na transferência e na aclamação.
 */
export default function ChurchFields({
  idPrefix,
  value,
  today,
  disabled = false,
  optional = false,
  withStatus = true,
  errors = {},
  onChange,
}: {
  idPrefix: string;
  value: ChurchData;
  today: string;
  disabled?: boolean;
  optional?: boolean;
  /** O formulário público mostra a situação como lista de opções, à parte. */
  withStatus?: boolean;
  errors?: { memberSince?: string; baptismDate?: string };
  onChange: (patch: Partial<ChurchData>) => void;
}) {
  const id = (field: string) => `${idPrefix}-${field}`;
  const since = membershipLabel(value.memberSince, today);

  return (
    <>
      {withStatus ? (
        <FormField id={id('status')} label="Situação" optional={optional}>
          <SimpleSelect
            id={id('status')}
            aria-label="Situação"
            disabled={disabled}
            ordered
            value={value.churchStatus ?? NONE}
            onValueChange={(v) => onChange({ churchStatus: v === NONE ? null : (v as ChurchStatus) })}
            options={STATUS_OPTIONS}
          />
        </FormField>
      ) : null}
      <FormField
        id={id('since')}
        label="Membro desde"
        optional={optional}
        error={errors.memberSince}
        hint={since || undefined}
      >
        <DateField
          id={id('since')}
          max={today}
          disabled={disabled}
          value={value.memberSince ?? ''}
          onChange={(iso) => onChange({ memberSince: iso || null })}
          {...fieldErrorProps(id('since'), errors.memberSince)}
        />
      </FormField>
      <FormField id={id('via')} label="Forma de ingresso" optional={optional}>
        <SimpleSelect
          id={id('via')}
          aria-label="Forma de ingresso"
          disabled={disabled}
          value={value.joinedVia ?? NONE}
          onValueChange={(v) => onChange({ joinedVia: v === NONE ? null : (v as JoinedVia) })}
          options={JOINED_OPTIONS}
        />
      </FormField>
      <FormField id={id('baptism')} label="Data de batismo" optional={optional} error={errors.baptismDate}>
        <DateField
          id={id('baptism')}
          max={today}
          disabled={disabled}
          value={value.baptismDate ?? ''}
          onChange={(iso) => onChange({ baptismDate: iso || null })}
          {...fieldErrorProps(id('baptism'), errors.baptismDate)}
        />
      </FormField>
      {showsOriginChurch(value.joinedVia) ? (
        <FormField id={id('origin')} label="Igreja de origem" optional={optional}>
          <Input
            id={id('origin')}
            placeholder="Nome da igreja anterior"
            disabled={disabled}
            value={value.originChurch}
            onChange={(e) => onChange({ originChurch: e.target.value })}
          />
        </FormField>
      ) : null}
    </>
  );
}
